import React from 'react';
import './SkeletonLoader.scss';

interface SkeletonLoaderProps {
  width?: string | number;
  height?: string | number;
  borderRadius?: string | number;
  variant?: 'text' | 'rectangular' | 'circular' | 'rounded';
  animation?: 'pulse' | 'wave' | 'none';
  className?: string;
  style?: React.CSSProperties;
  count?: number;
  spacing?: string | number;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  width = '100%',
  height = '20px',
  borderRadius,
  variant = 'rectangular',
  animation = 'pulse',
  className = '',
  style = {},
  count = 1,
  spacing = '8px'
}) => {
  // Default border radius based on variant
  const getDefaultBorderRadius = () => {
    switch (variant) {
      case 'text':
        return '4px';
      case 'circular':
        return '50%';
      case 'rounded':
        return '8px';
      case 'rectangular':
      default:
        return '4px';
    }
  };

  // Default dimensions based on variant
  const getDefaultDimensions = () => {
    switch (variant) {
      case 'text':
        return { width: width || '100%', height: height || '16px' };
      case 'circular':
        return { width: width || '40px', height: height || '40px' };
      case 'rounded':
        return { width: width || '100%', height: height || '40px' };
      case 'rectangular':
      default:
        return { width, height };
    }
  };

  const dimensions = getDefaultDimensions();
  const finalBorderRadius = borderRadius || getDefaultBorderRadius();

  const skeletonStyle: React.CSSProperties = {
    width: dimensions.width,
    height: dimensions.height,
    borderRadius: finalBorderRadius,
    backgroundColor: '#f0f0f0',
    ...style
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing
  };

  const skeletonClass = `skeleton-loader skeleton-${variant} skeleton-${animation} ${className}`;

  if (count === 1) {
    return <div className={skeletonClass} style={skeletonStyle} />;
  }

  return (
    <div style={containerStyle}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className={skeletonClass} style={skeletonStyle} />
      ))}
    </div>
  );
};

export default SkeletonLoader;